import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { smoothScrollHash } from "../lib/smoothScroll";

const SECTION_NAV = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Why ZOZO", href: "#why" },
  { label: "FAQ", href: "#faq" },
];
const INTERNAL_NAV = [
  { label: "Name Check", href: "/name-check", internal: true },
  { label: "For NRIs", href: "/nri", internal: true },
  { label: "Blog", href: "/blog", internal: true },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  // On non-home routes, hash anchors need to point back to home
  const prefix = isHome ? "" : "/";
  const NAV = [
    ...SECTION_NAV.map((n) => ({ ...n, href: `${prefix}${n.href}` })),
    ...INTERNAL_NAV,
  ];
  const ctaHref = isHome ? "#lead-form" : "/#lead-form";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full border-b transition-all duration-200 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-black/10"
          : "bg-white border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 bg-[#EAB308]" />
            <span className="font-heading font-black text-lg sm:text-xl tracking-tight text-[#EAB308]">
              ZOZO VENTURE
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) =>
              n.internal ? (
                <Link
                  key={n.href}
                  to={n.href}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm font-medium text-black/70 hover:text-black transition-colors"
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={smoothScrollHash}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm font-medium text-black/70 hover:text-black transition-colors"
                >
                  {n.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={ctaHref}
              onClick={smoothScrollHash}
              data-testid="header-cta-button"
              className="px-5 py-2.5 bg-[#EAB308] text-white text-sm font-semibold hover:bg-black transition-colors"
            >
              Get Free Consultation →
            </a>
          </div>

          <button
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/10 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) =>
              n.internal ? (
                <Link
                  key={n.href}
                  to={n.href}
                  onClick={() => setOpen(false)}
                  className="py-2 text-base font-medium text-black/80 hover:text-black"
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={(e) => { setOpen(false); smoothScrollHash(e); }}
                  className="py-2 text-base font-medium text-black/80 hover:text-black"
                >
                  {n.label}
                </a>
              )
            )}
            <a
              href={ctaHref}
              onClick={(e) => { setOpen(false); smoothScrollHash(e); }}
              data-testid="mobile-cta-button"
              className="mt-2 px-5 py-3 bg-[#EAB308] text-white text-sm font-semibold text-center"
            >
              Get Free Consultation →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
